import type { BookingPortalCatalog, BookingResource, BookingService } from "./clientPortalApi";
import type { PortalLocale } from "./portalPreferences";
import { createPortalTranslator } from "../i18n/messages";

export type ServiceChoice = {
  key: string;
  portalUuid: string;
  portalName: string;
  timezone: string;
  service: BookingService;
  available: boolean;
  unavailableMessage: string | null;
};

export function serviceChoices(catalogs: BookingPortalCatalog[]): ServiceChoice[] {
  return catalogs.flatMap((catalog) => catalog.services.map((service) => ({
    key: `${catalog.uuid}:${service.uuid}`,
    portalUuid: catalog.uuid,
    portalName: catalog.name,
    timezone: catalog.timezone ?? "Europe/Prague",
    service,
    available: catalog.available && service.available,
    unavailableMessage: service.unavailableMessage ?? catalog.unavailableMessage,
  })));
}

export function availableResources(choices: ServiceChoice[]): BookingResource[] {
  return [...new Map(choices.filter((choice) => choice.available)
    .flatMap(({ service }) => service.resources.filter((resource) => resource.available))
    .map((resource) => [resource.uuid, resource])).values()];
}

export const portalTimeZone = "Europe/Prague";
export const dateLabel = (value: string, timezone = portalTimeZone, locale: PortalLocale = "cs") => new Intl.DateTimeFormat(locale === "cs" ? "cs-CZ" : "en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: timezone }).format(new Date(value));
export const timeLabel = (value: string, timezone = portalTimeZone, locale: PortalLocale = "cs") => new Intl.DateTimeFormat(locale === "cs" ? "cs-CZ" : "en-GB", { hour: "2-digit", minute: "2-digit", timeZone: timezone }).format(new Date(value));
export function dateKey(value: string, timezone = portalTimeZone): string {
  const parts = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: timezone }).formatToParts(new Date(value));
  const values = Object.fromEntries(parts.map(({type, value}) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function safeWebUrl(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  try { const url = new URL(value); return ["https:", "http:"].includes(url.protocol) ? url.href : undefined; } catch { return undefined; }
}

export function safePortalImageUrl(value: string | null | undefined): string | undefined {
  if (value && /^\/media-content\/public\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    return value;
  }

  return safeWebUrl(value);
}

export function initials(value: string): string {
  return value.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toLocaleUpperCase("cs-CZ");
}

export function resourcePrice(resource: BookingResource | null | undefined, service: BookingService): string | null {
  return resource?.price ?? service.price;
}

export function formatPrice(value: string | null, currencyCode: string, locale: PortalLocale = "cs"): string | null {
  if (value === null) return null;
  const amount = Number(value);
  return Number.isFinite(amount)
    ? new Intl.NumberFormat(locale === "cs" ? "cs-CZ" : "en-GB", { style: "currency", currency: currencyCode }).format(amount)
    : `${value} ${currencyCode}`;
}

export function servicePrice(service: BookingService, locale: PortalLocale = "cs"): string | null {
  const prices = service.resources.filter((resource) => resource.available).map((resource) => resourcePrice(resource, service));
  if (!prices.length) return formatPrice(service.price, service.currencyCode, locale);
  const values = prices.filter((value): value is string => value !== null);
  if (!values.length) return null;
  if (new Set(prices).size === 1) return formatPrice(values[0], service.currencyCode, locale);
  const numeric = values.map(Number).filter(Number.isFinite);
  return numeric.length === values.length
    ? createPortalTranslator(locale)("reservation.priceFrom", { price: formatPrice(String(Math.min(...numeric)), service.currencyCode, locale) ?? "" })
    : formatPrice(service.price, service.currencyCode, locale);
}

export function resourceServiceChoices(choices: ServiceChoice[], resourceUuid: string, preferredKey?: string): ServiceChoice[] {
  const services = new Map<string, ServiceChoice>();
  for (const choice of choices) {
    if (!choice.available || !choice.service.resources.some((resource) => resource.uuid === resourceUuid && resource.available)) continue;
    if (!services.has(choice.service.uuid) || choice.key === preferredKey) services.set(choice.service.uuid, choice);
  }
  return [...services.values()];
}
